/**
 * MCP tools for workflow management.
 *
 * Mirrors `canary workflow list` and `canary workflow get`.
 *
 * @module
 */

import { mcpApiRequest } from '../api-client';
import { toolJson } from '../tool-helpers';

export function getWorkflowToolDefinitions() {
  return [
    {
      name: 'canary_workflow_list',
      description:
        'List workflows in the current organization. Supports filtering by name, type, and status with pagination.',
      inputSchema: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description: 'Search workflows by name',
          },
          flowType: {
            type: 'string',
            enum: ['standard', 'setup', 'seed', 'teardown'],
            description: 'Filter by flow type',
          },
          status: {
            type: 'string',
            enum: ['draft', 'approved'],
            description:
              "Filter by approval status. 'draft' = not yet approved; 'approved' = a human verified the test checks the right thing. Suite inclusion is the separate `promotion` axis (promoted/paused, managed via `canary workflow promote|pause`), and reliability (passing/flaky/degraded) is the separate `health` dimension.",
          },
          page: {
            type: 'number',
            description: 'Page number (default: 1)',
          },
          pageSize: {
            type: 'number',
            description: 'Page size (default: 25)',
          },
        },
      },
    },
    {
      name: 'canary_workflow_get',
      description:
        'Get a workflow definition with its steps, nodes, and configuration details.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID',
          },
        },
        required: ['workflowId'],
      },
    },
    {
      name: 'canary_workflow_patch',
      description:
        'Update workflow properties using merge-patch. Only provided fields are updated; omitted fields are left unchanged.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID',
          },
          name: {
            type: 'string',
            description: 'New workflow name',
          },
          description: {
            type: 'string',
            description: 'New description',
          },
          status: {
            type: 'string',
            enum: ['draft', 'approved'],
            description:
              "New approval status. 'approved' asserts a HUMAN verified this test checks the right thing — only set it when the user has personally confirmed. Approval does NOT put the test into suites; suite inclusion is the separate promote/pause axis (use `canary workflow promote|pause`). Reliability is tracked automatically by the `health` dimension.",
          },
          flowType: {
            type: 'string',
            enum: ['standard', 'setup', 'seed', 'teardown'],
            description: 'New flow type',
          },
        },
        required: ['workflowId'],
      },
    },
    {
      name: 'canary_workflow_run',
      description:
        'Trigger a workflow execution. Returns the run ID for tracking status via canary_workflow_runs_get.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID to execute',
          },
        },
        required: ['workflowId'],
      },
    },
    {
      name: 'canary_workflow_sync',
      description:
        'Atomically replace all nodes and edges in a workflow. Automatically creates a draft snapshot of the current state before overwriting so changes can be reverted. Use canary_workflow_get to read the current DAG first, modify nodes/edges, then sync back. When using propose=true on an existing workflow, REUSE each existing node\'s `id` for steps you keep (omit `id` only for genuinely new steps). Without ID reuse the proposal will diff as a full rewrite — every step shown as Removed+Added in the review UI even when only configs changed.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID',
          },
          nodes: {
            type: 'array',
            description: 'Array of node objects with id, nodeType, orderIndex, configJson',
            items: { type: 'object' },
          },
          edges: {
            type: 'array',
            description: 'Array of edge objects with id, sourceNodeId, targetNodeId, edgeType',
            items: { type: 'object' },
          },
          createSnapshot: {
            type: 'boolean',
            description: 'Create a draft snapshot before syncing (default: true)',
            default: true,
          },
          propose: {
            type: 'boolean',
            description: 'Create a proposal instead of applying directly. The proposal can be reviewed and accepted/rejected via the UI. IMPORTANT: when proposing on an existing workflow, reuse each existing node\'s `id` for steps you intend to keep — only omit `id` for genuinely new steps. Otherwise the diff will show every step as Removed+Added.',
            default: false,
          },
          proposalTitle: {
            type: 'string',
            description: 'Title for the proposal (used with propose: true)',
          },
          proposalDescription: {
            type: 'string',
            description: 'Description/rationale for the proposal (used with propose: true)',
          },
        },
        required: ['workflowId', 'nodes', 'edges'],
      },
    },
  ];
}

export async function handleWorkflowTool(
  tool: string,
  args: Record<string, unknown>
) {
  if (tool === 'canary_workflow_list') {
    try {
      const params = new URLSearchParams();
      if (args.search) params.set('search', String(args.search));
      if (args.flowType) params.set('flowType', String(args.flowType));
      if (args.status) params.set('status', String(args.status));
      if (args.page) params.set('page', String(args.page));
      if (args.pageSize) params.set('pageSize', String(args.pageSize));

      const qs = params.toString();
      const path = `/workflows${qs ? `?${qs}` : ''}`;
      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: unknown[];
        pagination: unknown;
      }>('GET', path);

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error ?? 'Unknown error' });
      }

      return toolJson({ ok: true, data: result.data, pagination: result.pagination });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_workflow_get') {
    try {
      const workflowId = args.workflowId as string;
      const result = await mcpApiRequest<{
        ok?: boolean;
        error?: string;
        workflow?: unknown;
        targetOrgId?: string;
        targetOrgName?: string;
      }>('GET', `/workflows/${workflowId}`);

      if (result.error) {
        return toolJson({ ok: false, error: result.error });
      }

      return toolJson({ ok: true, workflow: result.workflow });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_workflow_patch') {
    try {
      const workflowId = args.workflowId as string;
      const body: Record<string, unknown> = {};
      if (args.name) body.name = String(args.name);
      if (args.description) body.description = String(args.description);
      if (args.status) body.status = String(args.status);
      if (args.flowType) body.flowType = String(args.flowType);

      const result = await mcpApiRequest<{
        ok?: boolean;
        error?: string;
        workflow?: unknown;
      }>('PUT', `/workflows/${workflowId}`, body);

      if (result.error) {
        return toolJson({ ok: false, error: result.error });
      }

      return toolJson({ ok: true, workflow: result.workflow });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_workflow_run') {
    try {
      const workflowId = args.workflowId as string;

      const result = await mcpApiRequest<{
        ok?: boolean;
        error?: string;
        runId?: string;
        streamId?: string;
      }>('POST', `/workflows/${workflowId}/execute`);

      if (result.error) {
        return toolJson({ ok: false, error: result.error });
      }

      return toolJson({ ok: true, runId: result.runId, streamId: result.streamId });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_workflow_sync') {
    try {
      const workflowId = args.workflowId as string;
      const nodes = args.nodes as unknown[];
      const edges = args.edges as unknown[];

      // --propose: create a proposal instead of applying directly
      if (args.propose) {
        const title = (args.proposalTitle as string) ?? `Sync proposal (${nodes.length} nodes)`;
        const description = (args.proposalDescription as string) ?? null;

        const result = await mcpApiRequest<{
          ok?: boolean;
          error?: string;
          data?: { id: string; title: string; status: string };
        }>('POST', `/workflows/${workflowId}/proposals`, {
          title,
          description,
          snapshotJson: { nodes, edges },
          source: 'api',
        });

        if (result.error) {
          return toolJson({ ok: false, error: result.error });
        }

        return toolJson({
          ok: true,
          proposalId: result.data?.id,
          title: result.data?.title,
          status: result.data?.status,
        });
      }

      const createSnapshot = args.createSnapshot !== false;

      const result = await mcpApiRequest<{
        ok?: boolean;
        error?: string;
        snapshotVersion?: string;
      }>('PUT', `/workflows/${workflowId}/draft-state`, {
        nodes,
        edges,
        createSnapshot,
      });

      if (result.error) {
        return toolJson({ ok: false, error: result.error });
      }

      return toolJson({
        ok: true,
        ...(result.snapshotVersion ? { snapshotVersion: result.snapshotVersion } : {}),
      });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return null;
}
